import React from "react";
import { useMutation, useQuery } from "react-query";
import { instance, useCatalogue } from "@clearabee/ui-sdk";
import { Button } from "components/Core/Button/Button";
import { Input } from "components/Core/Input/Input";
import { useTranslation } from "react-i18next";
import {
  displayErrorMessage,
  FlexGrid,
  Message,
  theme,
  Text,
  Link,
} from "@clearabee/ui-library-native";
import { z } from "zod";
import { useForm, Form } from "lib/hooks/useForm";
import { Spinner } from "components/Core/Spinner/Spinner";
import { Platform, View } from "react-native";
import { useQuestionContext } from "lib/providers/questionProvider";
import { useState } from "react";
import { FormState } from "./Skips.validation";
import { useAuth } from "lib/hooks/useAuth";
import { postcode } from "lib/schemas";
import { JobSelectionCard } from "components/JobSelectionCard/JobSelectionCard";

export const RefAndPostcode = (): React.ReactElement => {
  const [translate] = useTranslation();
  const context = useQuestionContext<FormState>();
  const [customError, setCustomError] = useState<string>();
  const { setCatalogue } = useCatalogue();
  const { user } = useAuth();
  const [manualLookup, setManualLookup] = useState(!user);

  const { data: jobs, isFetching } = useQuery(
    ["getSkipExchangeJobs", user],
    async () => {
      const { data } = await instance.jobs.getSkipExchangeJobs();
      return data.items;
    },
    {
      enabled: !!user,
      refetchOnWindowFocus: false,
      onSuccess: (data) => {
        if (!data.length) setManualLookup(true);
      },
    },
  );

  const form = useForm({
    defaultValues: {
      ref: context.get("refAndPostcode")?.ref ?? "",
      postcode: context.get("refAndPostcode")?.postcode ?? "",
      previousSkip: context.get("refAndPostcode")?.previousSkip ?? "",
      selectedJob: undefined,
    },
    validationSchema: z.object({
      ref: manualLookup
        ? z
            .string()
            .min(1)
            .transform((value) => value.split(" ").join(""))
        : z.string().min(1),
      postcode: manualLookup ? postcode : z.string(),
      previousSkip: z.string().optional(),
      selectedJob: manualLookup ? z.number().optional() : z.number(),
    }),
    onSubmit: (value) => mutate(value),
  });
  const {
    mutate,
    isLoading,
    error,
    reset: resetMutation,
  } = useMutation(
    async ({ ref, postcode }: { ref: string; postcode: string }) => {
      setCustomError(undefined);
      const { data } = await instance.jobs.getJobByPostcodeAndRef(
        encodeURIComponent(ref),
        postcode,
        { params: { exchange: true } },
      );
      if (
        data?.items?.find(({ sku }) => sku.includes("SKIP")) &&
        data.status === "Started"
      ) {
        const { data: postcodeCatalogue } =
          await instance.catalogues.getCatalogue("hwa", {
            params: { postcode, flatten: true },
          });
        setCatalogue(data.addressPostcode, postcodeCatalogue);

        return context
          .set("refAndPostcode", {
            ref: data.ref,
            postcode: data.addressPostcode,
            previousSkip: data.items[0].sku ?? "",
          })
          .open("wasteType");
      } else {
        setCustomError(translate("hwa.errors.refAndPostcodeFetch"));
      }
    },
    {
      retry: 0,
    },
  );

  const { setValue, submitForm, reset } = form;

  return (
    <View style={{ alignItems: "center" }}>
      <Form state={form} useForm={false}>
        {({ Field }) => (
          <>
            <FlexGrid rowSpacing={null}>
              {isFetching && (
                <Spinner
                  size="large"
                  color="accent"
                  style={{ marginTop: theme.spacing.medium }}
                />
              )}
              {!isFetching && (
                <>
                  {!manualLookup && (
                    <FlexGrid.Cell base="100%">
                      <Text
                        color={Platform.OS === "web" ? "light" : "dark"}
                        styles={{
                          marginHorizontal: "auto",
                          textAlign: "center",
                        }}
                      >
                        {translate(
                          "hwa.screens.selectProduct.selectSkipExchangeDisclaimer",
                        )}
                      </Text>
                      <Field
                        name="selectedJob"
                        styles={{
                          maxWidth: 400,
                          marginTop: theme.spacing.medium,
                          marginHorizontal: "auto",
                        }}
                        invertErrorColors
                        errorBackground
                      >
                        {({ field }) => (
                          <>
                            {jobs?.map((item, index) => (
                              <JobSelectionCard
                                key={item.ref}
                                job={item}
                                active={field.value === index}
                                onPress={() => {
                                  field.onChange(index),
                                    setValue(
                                      "previousSkip",
                                      item.items?.find((i) =>
                                        i.sku.includes("YDGWSKIP"),
                                      )?.sku ?? "",
                                    );
                                  setValue("postcode", item.addressPostcode);
                                  setValue("ref", item.ref);
                                }}
                              />
                            ))}
                          </>
                        )}
                      </Field>
                    </FlexGrid.Cell>
                  )}
                  {manualLookup && (
                    <>
                      <FlexGrid.Cell base="50%">
                        <Field
                          name="ref"
                          errorStyles={Platform.select({
                            web: {
                              position: "absolute",
                              bottom: -29,
                            },
                            default: {
                              marginBottom: -theme.spacing.xsmall,
                            },
                          })}
                          labelStyles={{
                            color:
                              Platform.OS === "web"
                                ? theme.colors.light.base
                                : theme.colors.dark.base,
                          }}
                          invertErrorColors
                          errorBackground
                          label={translate("common.ref")}
                        >
                          {({ field }) => (
                            <Input.Text
                              {...field}
                              disabled={isLoading}
                              placeholder={translate(
                                "common.form.placeholder.ref",
                              )}
                            />
                          )}
                        </Field>
                      </FlexGrid.Cell>
                      <FlexGrid.Cell base="50%">
                        <Field
                          name="postcode"
                          errorStyles={Platform.select({
                            web: {
                              position: "absolute",
                              bottom: -29,
                            },
                            default: {
                              marginBottom: -theme.spacing.xsmall,
                            },
                          })}
                          labelStyles={{
                            color:
                              Platform.OS === "web"
                                ? theme.colors.light.base
                                : theme.colors.dark.base,
                          }}
                          invertErrorColors
                          errorBackground
                          label={translate("common.postcode")}
                        >
                          {({ field }) => (
                            <Input.Text
                              {...field}
                              disabled={isLoading}
                              placeholder={translate(
                                "common.form.placeholder.postcode",
                              )}
                            />
                          )}
                        </Field>
                      </FlexGrid.Cell>
                    </>
                  )}
                </>
              )}
              {!!jobs?.length && !isFetching && (
                <FlexGrid.Cell base="100%">
                  <Link
                    underline
                    color={Platform.OS === "web" ? "accent" : "brand"}
                    styles={{ marginTop: theme.spacing.small }}
                    onPress={() => {
                      setManualLookup(!manualLookup);
                      setCustomError(undefined);
                      resetMutation();
                      reset();
                    }}
                  >
                    {!manualLookup && "Switch to manual lookup?"}
                    {manualLookup && "Go Back"}
                  </Link>
                </FlexGrid.Cell>
              )}
            </FlexGrid>
            {displayErrorMessage(customError, (props) => (
              <View style={{ marginTop: theme.spacing.medium }}>
                <Message
                  {...props}
                  type="error"
                  styles={{
                    backgroundColor: theme.colors.negative.base,
                  }}
                  color="light"
                  background
                >
                  {props.children}
                </Message>
              </View>
            ))}
            {displayErrorMessage(error, (props) => (
              <View style={{ marginTop: theme.spacing.medium }}>
                <Message
                  {...props}
                  type="error"
                  styles={{
                    backgroundColor: theme.colors.negative.base,
                  }}
                  color="light"
                  background
                >
                  {props.children}
                </Message>
              </View>
            ))}
            {!isFetching && (
              <Button
                disabled={isLoading}
                type="button"
                color="warning"
                size="medium"
                styles={{
                  marginTop: theme.spacing.medium,
                  height: 54,
                }}
                onClick={submitForm}
              >
                {isLoading ? <Spinner /> : translate("common.continue")}
              </Button>
            )}
          </>
        )}
      </Form>
    </View>
  );
};
